import { useUpdateEffect } from "@chakra-ui/react";
import { TOAST_VARIANT } from "@root/constants";
import { useMainChatContext } from "@root/context/kps-chat-context";
import { selectThreads } from "@root/store/selectors/entities";
import {
  updateThreadList,
  updateThreadPage,
} from "@root/store/slices/entities";
import {
  showAlert,
  updatePage,
  updateToogleFilter,
} from "@root/store/slices/features";
import { DeleteThreads, getThreads } from "@root/store/thunks";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";

function useChatHistory() {
  const dispatch = useDispatch();
  const [loading, setloading] = useState(false);
  const navigate = useNavigate();
  const threads: any = useSelector(selectThreads);
  const [dates, setDates] = useState([]);
  const { t } = useTranslation("common");
  const [search, setSearch] = useState("");

  const { getInitialThreads } = useMainChatContext();

  const fetchThreads = useCallback(
    ({ ...props }: any = {}) => {
      setloading(true);
      dispatch(
        getThreads({
          params: { ...props },
          onSuccess: () => setloading(false),
          onError: () => setloading(false),
        })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    fetchThreads({
      page: 1,
    });
  }, [fetchThreads]);

  const onChatDeleteError = () => {
    dispatch(
      showAlert({
        type: TOAST_VARIANT.ERROR,
        message: t("feedback.somethingWrong"),
      })
    );
  };

  const onClearChat = ({ chat, allChats }: any) => {
    let ids: any[] = [];
    ids = chat ? [chat?.id] : allChats?.map(Number);
    dispatch(
      DeleteThreads({
        payload: { ids },
        onSuccess: () => {
          fetchThreads({});
          getInitialThreads();
        },
        onError: onChatDeleteError,
      })
    );
  };

  const handleChatClick = ({ chat }: any) => {
    dispatch(updateThreadPage(1 as any));
    dispatch(updateThreadList([] as any));
    navigate(`/chat/${chat?.id}`);
  };

  const debounced = useDebouncedCallback((value) => {
    const [start_date, end_date] = dates;
    fetchThreads({ searchQuery: value, start_date, end_date });
    dispatch(updatePage(1 as any));
  }, 1000);

  const onSearchChange = (e: any) => {
    dispatch(updateToogleFilter(e?.target?.value ? true : (false as any)));
    debounced(e?.target?.value);
    setSearch(e?.target?.value);
  };

  const onKeyDown = () => {
    return null;
  };

  const onDateSelect = (selectedDate: any) => {
    dispatch(updateToogleFilter(selectedDate ? true : (false as any)));
    setDates(selectedDate);
  };

  const onClear = () => {
    setDates([]);
    fetchThreads({
      searchQuery: search ? search : undefined,
    });
  };

  useUpdateEffect(() => {
    const [start_date, end_date] = dates;
    if (start_date && end_date) {
      dispatch(updatePage(1 as any));
      fetchThreads({
        start_date,
        end_date,
      });
    }
  }, [dates, dispatch, fetchThreads]);

  const refreshThreads = useCallback(() => {
    const [start_date, end_date] = dates;
    fetchThreads({
      searchQuery: search || undefined,
      start_date,
      end_date,
    });
  }, [dates, fetchThreads, search]);

  return {
    threads,
    loading,
    onClearChat,
    handleChatClick,
    fetchThreads,
    refreshThreads,
    onSearchChange,
    onKeyDown,
    onDateSelect,
    onClear,
    dates,
  };
}

export default useChatHistory;
